import { useContext, useEffect, useRef, useState } from "react";
import { getCoachAIToken, coachAIhandshake } from "../apis/callLogs";
import SharedDataContext from "../SharedDataContext";

export const useSessionManager = () => {
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [events, setEvents] = useState([]);
  const [dataChannel, setDataChannel] = useState(null);
  const [error, setError] = useState(null);
  const [clientMessages, setClientMessages] = useState([]);
  const [serverMessages, setServerMessages] = useState([]);
  const peerConnection = useRef(null);
  const audioElement = useRef(null);
  const localMediaStreamRef = useRef(null);

  const stopLocalAudioStream = () => {
    const stream = localMediaStreamRef.current;
    if (!stream) return;

    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (err) {
        console.error("Failed to stop media track:", err);
      }
    });

    localMediaStreamRef.current = null;
  };

  const resetSessionState = ({ preserveError = false } = {}) => {
    setIsSessionActive(false);
    setDataChannel(null);
    peerConnection.current = null;
    setEvents([]);
    setClientMessages([]);
    setServerMessages([]);
    stopLocalAudioStream();
    audioElement.current = null;
    if (!preserveError) {
      setError(null);
    }
  };

  const startSession = async ({ botId } = {}) => {
    setError(null);

    let pc;
    let localDataChannel;

    try {
      if (!botId) {
        setError("Please select a CoachAI bot before starting a session.");
        return;
      }

      const tokenResponse = await getCoachAIToken({
        botId,
        options: {
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
            "X-Workspace-Id": activeWorkspace?.id,
          },
        },
      });

      const ephemeralKey = tokenResponse?.client_secret?.value;
      if (!ephemeralKey) {
        setError("Unable to start session. Missing CoachAI token.");
        return;
      }

      pc = new RTCPeerConnection();
      peerConnection.current = pc;

      audioElement.current = document.createElement("audio");
      audioElement.current.autoplay = true;

      pc.ontrack = (event) => {
        if (audioElement.current) {
          const [stream] = event.streams;
          audioElement.current.srcObject = stream;
        }
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const [audioTrack] = mediaStream.getAudioTracks();
      localMediaStreamRef.current = mediaStream;
      pc.addTrack(audioTrack, mediaStream);

      localDataChannel = pc.createDataChannel("response");
      setDataChannel(localDataChannel);

      localDataChannel.onopen = () => {
        const sessionUpdate = {
          type: "session.update",
          session: {
            modalities: ["text", "audio"],
            tools: [],
            input_audio_transcription: {
              model: "whisper-1",
            },
          },
        };
        localDataChannel.send(JSON.stringify(sessionUpdate));
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const remoteSdp = await coachAIhandshake({
        data: offer.sdp,
        options: {
          headers: {
            Authorization: `Bearer ${ephemeralKey}`,
            "Content-Type": "application/sdp",
          },
          responseType: "text",
          transformResponse: [(data) => data],
        },
      });

      await pc.setRemoteDescription({
        type: "answer",
        sdp: remoteSdp,
      });
    } catch (err) {
      console.error("Failed to start session:", err);
      setError("Failed to start session. Please try again.");

      if (localDataChannel) {
        try {
          localDataChannel.close();
        } catch (closeError) {
          console.error("Failed to close data channel:", closeError);
        }
      }

      if (pc) {
        try {
          pc.close();
        } catch (closeError) {
          console.error("Failed to close peer connection:", closeError);
        }
      }

      if (audioElement.current) {
        audioElement.current.srcObject = null;
      }

      resetSessionState({ preserveError: true });
    }
  };

  const stopSession = () => {
    if (dataChannel) {
      try {
        dataChannel.close();
      } catch (closeError) {
        console.error("Failed to close data channel:", closeError);
      }
    }

    if (peerConnection.current) {
      try {
        peerConnection.current.close();
      } catch (closeError) {
        console.error("Failed to close peer connection:", closeError);
      }
    }

    if (audioElement.current) {
      audioElement.current.srcObject = null;
    }

    resetSessionState();
  };

  const sendClientEvent = (message) => {
    if (!dataChannel) {
      console.error("Failed to send message - no data channel available", message);
      return;
    }

    const eventId = message?.event_id || crypto.randomUUID();
    const payload = { ...message, event_id: eventId };
    dataChannel.send(JSON.stringify(payload));
    setEvents((prev) => [payload, ...prev]);
  };

  const sendTextMessage = (message) => {
    const event = {
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [{ type: "input_text", text: message }],
      },
    };

    const now = Date.now();
    const transcriptionMessage = {
      text: message,
      timestamp: now,
      role: "client",
    };
    setClientMessages((prev) => [...prev, transcriptionMessage]);

    sendClientEvent(event);
    sendClientEvent({ type: "response.create" });
  };

  useEffect(() => {
    if (!dataChannel) return;

    const handleMessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        setEvents((prev) => [parsed, ...prev]);

        const extractCleanText = (text) => {
          if (!text) return "";

          let cleanText = text;
          const sayMatch = text.match(/SAY:\s*(.*?)(?:\s*EVENTS:|$)/s);
          if (sayMatch && sayMatch[1]) {
            cleanText = sayMatch[1].trim();
          }

          if (cleanText === text) {
            cleanText = text
              .replace(/^SAY:\s*/, "")
              .replace(/\s*EVENTS:.*$/s, "")
              .trim();
          }

          return cleanText;
        };

        if (parsed.type === "response.audio_transcript.done" && parsed.transcript) {
          const cleanText = extractCleanText(parsed.transcript);
          if (cleanText) {
            const now = Date.now();
            const transcriptionMessage = {
              text: cleanText,
              timestamp: now,
              role: "server",
            };

            setServerMessages((prev) => {
              const isDuplicate = prev.some(
                (msg) =>
                  msg.text === cleanText && msg.role === "server" && Math.abs(msg.timestamp - now) < 2000
              );

              return isDuplicate ? prev : [...prev, transcriptionMessage];
            });
          }
        }

        if (parsed.type === "conversation.item.input_audio_transcription.completed" && parsed.transcript) {
          const cleanText = parsed.transcript.trim();
          if (cleanText) {
            const now = Date.now();
            const transcriptionMessage = {
              text: cleanText,
              timestamp: now,
              role: "client",
            };

            setClientMessages((prev) => {
              const isDuplicate = prev.some(
                (msg) =>
                  msg.text === cleanText && msg.role === "client" && Math.abs(msg.timestamp - now) < 2000
              );

              return isDuplicate ? prev : [...prev, transcriptionMessage];
            });
          }
        }
      } catch (err) {
        console.error("Failed to parse message:", err);
      }
    };

    const handleOpen = () => {
      setIsSessionActive(true);
      setEvents([]);
    };

    const handleError = (event) => {
      console.error("Data channel error:", event);
      setError("Connection error occurred. Please try again.");
    };

    const handleClose = () => {
      setIsSessionActive(false);
    };

    dataChannel.addEventListener("message", handleMessage);
    dataChannel.addEventListener("open", handleOpen);
    dataChannel.addEventListener("error", handleError);
    dataChannel.addEventListener("close", handleClose);

    return () => {
      dataChannel.removeEventListener("message", handleMessage);
      dataChannel.removeEventListener("open", handleOpen);
      dataChannel.removeEventListener("error", handleError);
      dataChannel.removeEventListener("close", handleClose);
    };
  }, [dataChannel]);

  return {
    isSessionActive,
    events,
    dataChannel,
    error,
    startSession,
    stopSession,
    sendTextMessage,
    serverMessages,
    clientMessages,
  };
};
